import { Caption1, DrawerBody, DrawerHeader, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, OverlayDrawer, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Dismiss24Regular, MoreHorizontal16Regular, Warning16Regular } from "@fluentui/react-icons";
import { useEffect, useState, type ReactNode } from "react";
import { AppDialog, ConfirmDialog, DialogCheckbox, DialogChoiceGroup, DialogField } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListToolbar } from "@/components/list-toolbar";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { addDays, todayISO } from "@/engine/date";
import { formatDate, formatDaysUntil } from "@/lib/date-format";
import {
  createMockShare,
  mockShareCopyText,
  mockShareViews,
  pauseImpactSentence,
  pauseMockShare,
  pauseOutOfPolicyShares,
  proposedShareExpiry,
  resumeMockShare,
  shareExceedsPolicy,
  shareMatchesFilter,
  sharePolicySentence,
  SHARE_LIST_FILTERS,
  revokeImpactSentence,
  revokeMockShare,
  setMockShareExpiry,
  shareExpirySoon,
  sharePreviewCaption,
  sharePreviewSummary,
  type MockShareLink,
} from "./mock-plan-access";
import { UI_TERMS } from "./terms";

const SCREENS = ["현황 보고", "일정(간트)", "일정(시트)"] as const;
const AUDIENCES = ["이메일 코드", "비밀번호", "멤버 로그인", "링크"] as const;

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  frame: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    borderTop: "none",
  },
  solid: { flexShrink: 0 },
  fixed: { tableLayout: "fixed" },
  clip: { overflow: "hidden", maxWidth: 0 },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  audience: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  soon: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  center: { textAlign: "center" },
  warn: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    color: tokens.colorNeutralForeground2,
  },
  warnIcon: {
    flex: "0 0 auto",
    color: tokens.colorPaletteDarkOrangeForeground1,
    marginTop: "1px",
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  policy: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
    marginTop: "8px",
    minWidth: 0,
    padding: "2px 2px 2px 8px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    color: tokens.colorNeutralForeground1,
  },
  policyIcon: {
    flexShrink: 0,
    color: tokens.colorPaletteYellowForeground1,
  },
  policyText: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  preview: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  nameBtn: {
    display: "block",
    width: "100%",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  drawer: {
    position: "fixed",
    top: "0",
    right: "0",
    height: "100vh",
    width: "360px",
    maxWidth: "100vw",
    pointerEvents: "auto",
    zIndex: 60,
  },
  drawerHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
  },
  drawerBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  viewRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
});

function shareSummary(share: MockShareLink) {
  const screens = share.screens?.join(" · ");
  return [screens, share.mode].filter(Boolean).join(" · ");
}

type SharePending =
  | { kind: "revoke" | "pause" | "resume"; share: MockShareLink }
  | { kind: "expire"; share: MockShareLink; expires: string };

/** 이 플랜의 공유 링크. 없으면 빈 안내. 복사·만료·일시 중지·철회를 담는다. */
export function ShareLinkList({
  shares,
  detailId = null,
  onDetailId,
  action,
}: {
  shares: MockShareLink[];
  detailId?: string | null;
  onDetailId?: (id: string | null) => void;
  /** 툴바 오른쪽의 본동사. */
  action?: ReactNode;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const layer = useDialogLayer();
  const [pending, setPending] = useState<SharePending | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("전체");
  const detail = shares.find((share) => share.id === detailId) ?? null;
  const setDetailId = onDetailId ?? (() => undefined);
  const outOfPolicy = shares.some((share) => share.status !== "일시 중지" && shareExceedsPolicy(share.expires));
  const shown = shares.filter((share) => shareMatchesFilter(share, filter));
  const menuProps = { mountNode: layer, positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const } };
  const target = pending?.share ?? null;

  const copyLink = (share: MockShareLink) => {
    setCopiedId(share.id);
    void navigator.clipboard?.writeText(mockShareCopyText(share.id)).catch(() => undefined);
  };

  return (
    <>
    <div className={styles.stack}>
    <ListToolbar
      edge="table"
      filters={
        shares.length > 0 ? (
          <FuiFilterSelect
            className={styles.solid}
            density="dialog"
            label="상태"
            ariaLabel="공유 상태 필터"
            value={filter}
            options={SHARE_LIST_FILTERS.map((value) => ({ value, label: value }))}
            onValueChange={setFilter}
            mountNode={layer}
          />
        ) : null
      }
      trail={action}
    />
    {shares.length === 0 ? (
      <GhostEmptyState compact title="아직 공유 링크가 없습니다" description="새 공유로 읽기 전용 링크를 만들 수 있습니다." />
    ) : shown.length === 0 ? (
      <GhostEmptyState compact title="일치하는 공유가 없습니다" description="상태를 바꿔 보세요." />
    ) : (
    <div className={mergeClasses(table.frame, styles.frame)}>
      <table className={mergeClasses(table.table, styles.fixed)} aria-label="공유 링크">
        <colgroup>
          <col />
          <col style={{ width: 108 }} />
          <col style={{ width: 112 }} />
          <col style={{ width: 56 }} />
          <col style={{ width: 52 }} />
        </colgroup>
        <thead>
          <tr>
            <th className={table.th}>이름</th>
            <th className={table.th}>접근</th>
            <th className={mergeClasses(table.th, table.num)}>만료</th>
            <th className={mergeClasses(table.th, table.num)}>열람</th>
            <th className={mergeClasses(table.th, styles.center)}>동작</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((share) => {
            const paused = share.status === "일시 중지";
            const outside = shareExceedsPolicy(share.expires);
            const summary = [shareSummary(share), paused ? "일시 중지" : "", outside ? "규정 밖" : ""].filter(Boolean).join(" · ");
            return (
              <tr key={share.id}>
                <td className={mergeClasses(table.td, styles.clip)}>
                  <button type="button" className={styles.nameBtn} title={share.name} aria-label={`${share.name} 자세히`} onClick={() => setDetailId(share.id)}>
                    {share.name}
                  </button>
                  {copiedId === share.id ? (
                    <Caption1 className={styles.meta}>복사했습니다</Caption1>
                  ) : summary ? (
                    <Caption1 className={outside ? styles.soon : styles.meta} title={summary}>{summary}</Caption1>
                  ) : null}
                </td>
                <td className={mergeClasses(table.td, styles.audience)} title={share.audience}>{share.audience}</td>
                <td className={mergeClasses(table.td, table.num)}>
                  <div>{formatDate(share.expires, "numeric")}</div>
                  <Caption1 className={shareExpirySoon(share.expires) ? styles.soon : styles.meta}>{formatDaysUntil(share.expires, todayISO())}</Caption1>
                </td>
                <td className={mergeClasses(table.td, table.num)}>{share.views}</td>
                <td className={mergeClasses(table.td, styles.center)}>
                  <Menu {...menuProps}>
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button" className={styles.menuBtn} aria-label={`${share.name} 동작`}>
                        <MoreHorizontal16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover className={styles.popover}>
                      <MenuList>
                        <MenuItem onClick={() => copyLink(share)}>링크 복사</MenuItem>
                        <MenuItem onClick={() => setPending({ kind: "expire", share, expires: proposedShareExpiry() })}>만료</MenuItem>
                        {paused ? (
                          <MenuItem onClick={() => setPending({ kind: "resume", share })}>재개</MenuItem>
                        ) : (
                          <MenuItem onClick={() => setPending({ kind: "pause", share })}>일시 중지</MenuItem>
                        )}
                        <MenuItem onClick={() => setPending({ kind: "revoke", share })}>철회</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    )}
    {outOfPolicy ? (
      <div className={styles.policy} role="status">
        <Warning16Regular className={styles.policyIcon} />
        <span className={styles.policyText}>규정을 넘는 링크가 있습니다.</span>
        <FuiButton appearance="outline" density="dialog" onClick={() => setPolicyOpen(true)}>
          일괄 일시 중지
        </FuiButton>
      </div>
    ) : null}
    </div>
    <ShareDetailDrawer share={detail} onClose={() => setDetailId(null)} />
    <ConfirmDialog
      open={pending?.kind === "revoke"}
      title="공유 철회"
      message={<>이 공유를 철회할까요? {revokeImpactSentence()}</>}
      confirmLabel="철회"
      cancelLabel="취소"
      onCancel={() => setPending(null)}
      onConfirm={() => {
        if (target && pending?.kind === "revoke") revokeMockShare(target.id);
        setPending(null);
      }}
    />
    <ConfirmDialog
      open={pending?.kind === "expire"}
      title="만료 변경"
      message="이 공유의 만료를 바꿀까요?"
      confirmLabel="바꾸기"
      cancelLabel="취소"
      onCancel={() => setPending(null)}
      onConfirm={() => {
        if (pending?.kind === "expire") setMockShareExpiry(pending.share.id, pending.expires);
        setPending(null);
      }}
    />
    <ConfirmDialog
      open={pending?.kind === "pause"}
      title="공유 일시 중지"
      message={<>이 공유를 일시 중지할까요? {pauseImpactSentence()}</>}
      confirmLabel="중지"
      cancelLabel="취소"
      onCancel={() => setPending(null)}
      onConfirm={() => {
        if (target && pending?.kind === "pause") pauseMockShare(target.id);
        setPending(null);
      }}
    />
    <ConfirmDialog
      open={policyOpen}
      title="공유 일시 중지"
      message={`일시 중지할까요? ${pauseImpactSentence()}`}
      confirmLabel="중지"
      cancelLabel="취소"
      onCancel={() => setPolicyOpen(false)}
      onConfirm={() => {
        pauseOutOfPolicyShares();
        setPolicyOpen(false);
      }}
    />
    <ConfirmDialog
      open={pending?.kind === "resume"}
      title="공유 재개"
      message="이 공유를 다시 열까요?"
      confirmLabel="재개"
      cancelLabel="취소"
      onCancel={() => setPending(null)}
      onConfirm={() => {
        if (target && pending?.kind === "resume") resumeMockShare(target.id);
        setPending(null);
      }}
    />
    </>
  );
}

/** 이름·화면·접근·모드를 고르고 목업 링크를 만든다. 고른 화면은 만들기 전에 미리 보여 준다. */
export function NewShareButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <FuiButton appearance="primary" density="dialog" onClick={() => setOpen(true)}>
        새 공유
      </FuiButton>
      <ShareCreateDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ShareCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("고객사 주간 보고");
  const [screens, setScreens] = useState<string[]>(["현황 보고", "일정(간트)"]);
  const [audience, setAudience] = useState<string>(AUDIENCES[0]);
  const [mode, setMode] = useState<string>(UI_TERMS.liveShare.ko);
  const ready = name.trim() !== "" && screens.length > 0;

  const close = () => {
    setName("고객사 주간 보고");
    setScreens(["현황 보고", "일정(간트)"]);
    setAudience(AUDIENCES[0]);
    setMode(UI_TERMS.liveShare.ko);
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={close}
      width="md"
      title="새 공유"
      description="읽기 전용 링크를 만듭니다."
      footer={
        <>
          <FuiButton appearance="outline" density="dialog" onClick={close}>
            취소
          </FuiButton>
          <FuiButton
            appearance="primary"
            density="dialog"
            disabled={!ready}
            onClick={() => {
              if (!createMockShare({ name, audience, mode, screens })) return;
              close();
            }}
          >
            링크 만들기
          </FuiButton>
        </>
      }
    >
      <ShareCreateFields
        name={name}
        screens={screens}
        audience={audience}
        mode={mode}
        onName={setName}
        onScreens={setScreens}
        onAudience={setAudience}
        onMode={setMode}
      />
    </AppDialog>
  );
}

function ShareCreateFields({
  name,
  screens,
  audience,
  mode,
  onName,
  onScreens,
  onAudience,
  onMode,
}: {
  name: string;
  screens: string[];
  audience: string;
  mode: string;
  onName: (value: string) => void;
  onScreens: (value: string[]) => void;
  onAudience: (value: string) => void;
  onMode: (value: string) => void;
}) {
  const styles = useStyles();
  const layer = useDialogLayer();
  const expires = addDays(todayISO(), 30);

  const toggleScreen = (screen: string, checked: boolean) => {
    onScreens(checked ? [...screens, screen] : screens.filter((item) => item !== screen));
  };

  return (
    <>
      <DialogField label="이름">
        <Input size="small" value={name} onChange={(_, data) => onName(data.value)} aria-label="공유 이름" />
      </DialogField>
      <DialogField label="화면">
        {SCREENS.map((screen) => (
          <DialogCheckbox key={screen} label={screen} checked={screens.includes(screen)} onChange={(checked) => toggleScreen(screen, checked)} />
        ))}
      </DialogField>
      <DialogField label="접근">
        <FuiFilterSelect
          density="dialog"
          label="접근"
          ariaLabel="공유 접근"
          value={audience}
          options={AUDIENCES.map((value) => ({ value, label: value }))}
          onValueChange={onAudience}
          mountNode={layer}
        />
      </DialogField>
      {audience === "링크" ? (
        <div className={styles.warn}>
          <Warning16Regular className={styles.warnIcon} />
          <Caption1>링크를 아는 누구나 볼 수 있습니다.</Caption1>
        </div>
      ) : null}
      <DialogChoiceGroup
        label="모드"
        ariaLabel="공유 모드"
        value={mode}
        onChange={onMode}
        options={[
          { value: UI_TERMS.liveShare.ko, label: UI_TERMS.liveShare.ko, hint: "저장할 때마다 발행본이 갱신됩니다." },
          { value: UI_TERMS.snapshotShare.ko, label: UI_TERMS.snapshotShare.ko, hint: "한 발행본에 묶여 바뀌지 않습니다." },
        ]}
      />
      <Caption1 className={styles.quiet}>만료 {formatDate(expires, "full")}</Caption1>
      <Caption1 className={styles.quiet}>정책: {sharePolicySentence()}</Caption1>
      <SharePreview screens={screens} mode={mode} />
    </>
  );
}

function SharePreview({ screens, mode }: { screens: string[]; mode: string }) {
  const styles = useStyles();
  const summary = sharePreviewSummary(screens, mode);
  return (
    <div className={styles.preview} aria-label="열람 미리보기">
      <Caption1 className={styles.quiet}>열람자가 보게 될 화면</Caption1>
      {summary ? (
        <>
          <div>{summary}</div>
          <Caption1 className={styles.quiet}>{sharePreviewCaption()}</Caption1>
        </>
      ) : (
        <Caption1>보여 줄 화면이 없습니다.</Caption1>
      )}
    </div>
  );
}

/** 공유 한 줄의 열람 기록. 이 브라우저의 목업이라 지우는 버튼은 없다. */
function ShareDetailDrawer({ share, onClose }: { share: MockShareLink | null; onClose: () => void }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const layer = useDialogLayer();
  const views = share ? mockShareViews(share.id) : [];

  useEffect(() => {
    if (!share) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [share, onClose]);

  if (!share || !layer) return null;
  const summary = [shareSummary(share), share.audience].filter(Boolean).join(" · ");
  return (
    <OverlayDrawer
      open
      mountNode={layer}
      position="end"
      modalType="modal"
      className={styles.drawer}
      style={{ position: "fixed", top: 0, right: 0, height: "100vh", zIndex: 60 }}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      aria-label="공유 열람 기록"
    >
      <div data-dialog-local-escape="">
        <DrawerHeader>
          <div className={styles.drawerHead}>
            <div className={styles.name} title={share.name}>{share.name}</div>
            <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={onClose} />
          </div>
        </DrawerHeader>
        <DrawerBody className={styles.drawerBody}>
          {summary ? <Caption1 className={styles.quiet}>{summary}</Caption1> : null}
          <div className={styles.viewRow}>
            <Caption1 className={styles.quiet}>만료</Caption1>
            <div className={table.num}>{formatDate(share.expires, "numeric")} {formatDaysUntil(share.expires, todayISO())}</div>
          </div>
          <div className={styles.viewRow}>
            <Caption1 className={styles.quiet}>열람</Caption1>
            <div className={table.num}>{share.views}</div>
          </div>
          <Caption1 className={styles.quiet}>최근 열람</Caption1>
          {views.length === 0 ? (
            <Caption1>아직 열람 기록이 없습니다.</Caption1>
          ) : (
            views.map((view) => (
              <div key={view.id} className={styles.viewRow}>
                <div>{formatDate(view.when, "dateTime")}</div>
                <Caption1 className={styles.quiet}>{view.device} · {view.audience}</Caption1>
              </div>
            ))
          )}
        </DrawerBody>
      </div>
    </OverlayDrawer>
  );
}
